import { getAuthHeaders } from "../utils/authHeaders";
import { API_URL } from "../config/api";

export interface Message {
  _id: string;
  conversationId: string;
  senderId: string | {
    _id: string;
    firstName: string;
    lastName: string;
  };
  content: string;
  read: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Conversation {
  _id: string;
  participants: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    uniName?: string;
    profession?: string;
  }[];
  applicationId?: string;
  jobId?: {
    _id: string;
    title: string;
    company: string;
  };
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface MessagesResponse {
  conversation: Conversation;
  messages: Message[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalMessages: number;
    limit: number;
  };
}

// Get all conversations for current user
export const getConversations = async (): Promise<Conversation[]> => {
  try {
    const response = await fetch(`${API_URL}/messages/conversations`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch conversations");
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching conversations:", error);
    throw error;
  }
};

// Get messages in a conversation
export const getMessages = async (
  conversationId: string,
  page: number = 1,
  limit: number = 50
): Promise<MessagesResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await fetch(
      `${API_URL}/messages/conversations/${conversationId}?${params}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch messages");
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching messages:", error);
    throw error;
  }
};

// Start a new conversation
export const startConversation = async (data: {
  recipientId: string;
  applicationId?: string;
  jobId?: string;
  initialMessage?: string;
}): Promise<Conversation> => {
  try {
    const response = await fetch(`${API_URL}/messages/conversations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error("Failed to start conversation");
    }

    return response.json();
  } catch (error) {
    console.error("Error starting conversation:", error);
    throw error;
  }
};

// Send a message
export const sendMessage = async (
  conversationId: string,
  content: string
): Promise<Message> => {
  try {
    const response = await fetch(
      `${API_URL}/messages/conversations/${conversationId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({ content }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to send message");
    }

    return response.json();
  } catch (error) {
    console.error("Error sending message:", error);
    throw error;
  }
};

// Get unread message count
export const getUnreadMessageCount = async (): Promise<number> => {
  try {
    const response = await fetch(`${API_URL}/messages/unread-count`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      return 0;
    }

    const data = await response.json();
    return data.unreadCount;
  } catch (error) {
    console.error("Error fetching unread count:", error);
    return 0;
  }
};

// Mark message as read
export const markMessageAsRead = async (messageId: string): Promise<Message> => {
  try {
    const response = await fetch(`${API_URL}/messages/${messageId}/read`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to mark message as read");
    }

    return response.json();
  } catch (error) {
    console.error("Error marking message as read:", error);
    throw error;
  }
};
